import { StandardEvents } from '@shopify/events';

/**
 * ONÉRO — caixinha de compra da PDP.
 * Copia cor/tamanho do seletor principal, repassa os cliques pra ele e usa o
 * ADD TO CART de lá. Assim galeria, preço e estoque seguem a lógica do tema.
 */
class OneroQuickbuy extends HTMLElement {
  #abort = new AbortController();
  #buyObserver = null;
  #footerObserver = null;
  #hiddenByFooter = false;
  #stuck = false;

  connectedCallback() {
    this.box = this.querySelector('.oqb__box');
    this.optionsEl = this.querySelector('[data-oqb-options]');
    this.atc = this.querySelector('[data-oqb-atc]');
    this.toggleBtn = this.querySelector('[data-oqb-toggle]');
    if (!this.box || !this.optionsEl || !this.atc) return;

    const { signal } = this.#abort;
    this.querySelector('.oqb__head')?.addEventListener('click', this.#onHeadClick, { signal });
    this.optionsEl.addEventListener('click', this.#onOptionClick, { signal });
    this.optionsEl.addEventListener('change', this.#onBundleChange, { signal });
    this.atc.addEventListener('click', this.#onAddClick, { signal });
    this.querySelector('[data-oqb-up]')?.addEventListener('click', this.#onUpClick, { signal });

    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.closest('.shopify-section')?.addEventListener('onero-bundle:update', () => this.#renderBundle(), { signal });
    if (this.#bundle) customElements.whenDefined('onero-bundle').then(() => this.#renderBundle());
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartDone, { signal });
    document.addEventListener(StandardEvents.cartError, this.#onCartDone, { signal });

    this.#buildOptions();
    if (this.#section) this.#syncImage(this.#section);
    this.#setupObservers();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#buyObserver?.disconnect();
    this.#footerObserver?.disconnect();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #picker() {
    return this.#section?.querySelector(`.product-details variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  get #form() {
    return this.#section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  get #mainAtc() {
    return this.#form?.querySelector('[ref="addToCartButton"]');
  }

  /** Bloco de ofertas da página (blocks/onero-bundle.liquid). Com ele, a caixinha vira um espelho do bundle. */
  get #bundle() {
    return this.#section?.querySelector('.product-details onero-bundle') ?? null;
  }

  /* ---------- aparecer / sumir ---------- */

  #setupObservers() {
    const buyBlock = this.#form?.closest('.buy-buttons-block');
    if (!buyBlock) return;

    const isAbove = () => {
      const rect = buyBlock.getBoundingClientRect();
      return rect.bottom < 0;
    };

    this.#buyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) {
        this.#hiddenByFooter = false;
        this.#setStuck(false);
      } else if (isAbove() && !this.#hiddenByFooter) {
        this.#setStuck(true);
      }
    });
    this.#buyObserver.observe(buyBlock);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;
    this.#footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          if (this.#stuck) {
            this.#hiddenByFooter = true;
            this.#setStuck(false);
          }
        } else if (this.#hiddenByFooter) {
          this.#hiddenByFooter = false;
          if (isAbove()) this.#setStuck(true);
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );
    this.#footerObserver.observe(footer);
  }

  #setStuck(value) {
    this.#stuck = value;
    this.box.dataset.stuck = String(value);
    if (!value) this.#setOpen(false);
    // 28/10: a seta fica logo acima da caixinha fechada
    if (value) this.style.setProperty('--oqb-h', `${this.box.offsetHeight}px`);
  }

  /** 28/10 (Kauã): seta verde do celular leva até o ADD TO BAG da página. */
  #onUpClick = () => {
    const target = this.#mainAtc ?? this.#form?.closest('.buy-buttons-block');
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  };

  #setOpen(value) {
    this.box.dataset.open = String(value);
    this.toggleBtn?.setAttribute('aria-expanded', String(value));
  }

  #onHeadClick = () => {
    this.#setOpen(this.box.dataset.open !== 'true');
  };

  /* ---------- opções (copiadas do seletor principal) ---------- */

  #buildOptions() {
    if (this.#bundle) {
      this.#renderBundle();
      return;
    }
    const picker = this.#picker;
    if (!picker) {
      this.optionsEl.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();
    picker.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
      if (!inputs.length) return;

      const name = inputs[0].dataset.optionName || '';
      const checked = inputs.find((input) => input.checked);
      const isSwatch = fieldset.classList.contains('variant-option--swatches');

      const group = document.createElement('div');
      group.className = 'oqb__opt';

      const label = document.createElement('p');
      label.className = 'oqb__label';
      label.textContent = `${name}:`;
      const current = document.createElement('span');
      current.textContent = checked ? checked.value : '';
      label.append(current);

      const values = document.createElement('div');
      values.className = `oqb__values${isSwatch ? ' oqb__values--swatch' : ''}`;

      for (const input of inputs) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = isSwatch ? 'oqb__sw' : 'oqb__sz';
        button.dataset.valueId = input.dataset.optionValueId || '';
        button.dataset.available = input.dataset.optionAvailable === 'false' ? 'false' : 'true';
        button.setAttribute('aria-pressed', String(input.checked));
        button.setAttribute('aria-label', input.getAttribute('aria-label') || input.value);

        if (isSwatch) {
          const swatch = input.closest('label')?.querySelector('.swatch');
          if (swatch) button.append(swatch.cloneNode(true));
          button.title = input.value;
        } else {
          button.textContent = input.value;
        }
        values.append(button);
      }

      group.append(label, values);
      fragment.append(group);
    });

    this.optionsEl.replaceChildren(fragment);
  }

  #onOptionClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-value-id]') : null;
    if (!button || button.getAttribute('aria-pressed') === 'true') return;

    const input = this.#picker?.querySelector(`input[data-option-value-id="${button.dataset.valueId}"]`);
    if (!input) return;

    // mostra a escolha na hora; o resto chega quando o tema terminar de atualizar
    button.parentElement?.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    button.setAttribute('aria-pressed', 'true');
    const current = button.closest('.oqb__opt')?.querySelector('.oqb__label span');
    if (current) current.textContent = input.value;

    input.click();
  };

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!this.#picker || !this.#picker.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const fresh = detail.html.querySelector('onero-quickbuy');
        const price = this.querySelector('[data-oqb-price]');
        const freshPrice = fresh?.querySelector('[data-oqb-price]');
        if (price && freshPrice && !this.#bundle) price.innerHTML = freshPrice.innerHTML;
        // a galeria da página troca logo depois desta promessa; copia a foto quando ela mudar
        for (const delay of [0, 200, 500, 1000]) {
          setTimeout(() => this.#section && this.#syncImage(this.#section), delay);
        }

        const available = Boolean(detail.resource?.available);
        this.dataset.available = String(available);
        this.atc.disabled = !available;
        const text = fresh?.querySelector('[data-oqb-atc-text]')?.textContent?.trim();
        const atcText = this.querySelector('[data-oqb-atc-text]');
        if (atcText && text) atcText.textContent = text;

        // o seletor principal já foi atualizado pelo tema antes desta promessa
        this.#buildOptions();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[onero-quickbuy]', error);
      });
  };

  /**
   * Foto do topo.
   * swatch (Envoy): a foto da amostra da cor escolhida, só a camisa.
   * portrait (resto): 1ª foto da galeria, que já mostra só as fotos da cor escolhida.
   */
  #syncImage(root) {
    const target = this.querySelector('[data-oqb-image] img');
    if (!target) return;

    let src;
    if (this.dataset.imageMode === 'swatch') {
      const style = this.#picker?.querySelector('.variant-option--swatches input:checked')?.closest('label')?.querySelector('.swatch')?.getAttribute('style') || '';
      src = style.match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/)?.[1];
    } else {
      src = root.querySelector('.product-information__media .product-media__image')?.getAttribute('src');
    }
    if (!src) return;
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', '160');
    target.src = url.toString();
    target.removeAttribute('srcset');
  }

  /* ---------- bundle (espelho do bloco onero-bundle da página) ---------- */

  #bundleKey = '';

  #renderBundle() {
    const bundle = this.#bundle;
    if (!bundle || typeof bundle.bundleState !== 'function') return;
    const { tiers, rows } = bundle.bundleState();
    const selected = tiers.find((tier) => tier.selected) || tiers[0];
    if (!selected) return;

    // preço do topo = preço da oferta escolhida, igual ao bundle. 28/10 (Kauã): o kit inteiro
    // (£65.00, riscado £99.90) com o selo verde da oferta ("Buy 1, get 1 free")
    const price = this.querySelector('[data-oqb-price]');
    if (price) {
      const wrap = document.createElement('span');
      wrap.className = 'oqb__bprice';
      const now = document.createElement('span');
      now.className = 'price';
      now.textContent = selected.price;
      wrap.append(now);
      if (selected.compare) {
        const was = document.createElement('s');
        was.className = 'oqb__bcompare';
        was.textContent = selected.compare;
        wrap.append(was);
      }
      if (selected.title) {
        const tag = document.createElement('span');
        tag.className = 'oqb__btag';
        tag.textContent = selected.title;
        wrap.append(tag);
      }
      price.replaceChildren(wrap);
    }

    const key = `${selected.value}:${rows.length}`;
    if (key !== this.#bundleKey || !this.optionsEl.querySelector('.oqb__tiers')) {
      this.#bundleKey = key;
      this.optionsEl.hidden = false;
      this.optionsEl.replaceChildren(this.#buildTiers(tiers), this.#buildRows(rows));
      return;
    }

    // mesma oferta: só atualiza valores, sem trocar os campos (não perde o foco)
    this.optionsEl.querySelectorAll('.oqb__rows [data-row]').forEach((rowEl, index) => {
      const row = rows[index];
      if (!row) return;
      const img = rowEl.querySelector('img');
      if (img && row.thumb && img.getAttribute('src') !== row.thumb) img.src = row.thumb;
      for (const role of ['color', 'size']) {
        const data = row[role];
        const select = rowEl.querySelector(`select[data-role="${role}"]`);
        if (!data || !(select instanceof HTMLSelectElement)) continue;
        [...select.options].forEach((option, i) => {
          option.disabled = Boolean(data.options[i]?.disabled);
        });
        if (select.value !== data.value) select.value = data.value;
      }
    });
  }

  #buildTiers(tiers) {
    const list = document.createElement('div');
    list.className = 'oqb__tiers';
    list.setAttribute('role', 'radiogroup');

    for (const tier of tiers) {
      const label = document.createElement('label');
      label.className = 'oqb__tier';
      label.dataset.selected = String(tier.selected);

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.className = 'obd__radio';
      radio.name = `oqb-tier-${this.dataset.sectionId}`;
      radio.value = tier.value;
      radio.checked = tier.selected;

      const dot = document.createElement('span');
      dot.className = 'obd__dot';
      dot.setAttribute('aria-hidden', 'true');

      const title = document.createElement('span');
      title.className = 'oqb__tier-title';
      title.textContent = tier.title;
      if (tier.badge) {
        const badge = document.createElement('span');
        badge.className = 'oqb__tier-badge';
        badge.textContent = tier.badge;
        title.append(badge);
      }

      const prices = document.createElement('span');
      prices.className = 'oqb__tier-prices';
      const now = document.createElement('span');
      now.className = 'oqb__tier-price';
      now.textContent = tier.price;
      prices.append(now);
      if (tier.compare) {
        const was = document.createElement('s');
        was.className = 'oqb__bcompare';
        was.textContent = tier.compare;
        prices.append(was);
      }

      label.append(radio, dot, title, prices);
      list.append(label);
    }
    return list;
  }

  #buildRows(rows) {
    const list = document.createElement('div');
    list.className = 'oqb__rows';

    rows.forEach((row, index) => {
      const rowEl = document.createElement('div');
      rowEl.className = 'obd__row';
      rowEl.dataset.row = '';

      const num = document.createElement('span');
      num.className = `obd__num${row.free ? ' obd__num--free' : ''}`;
      num.textContent = row.label;

      const thumb = document.createElement('span');
      thumb.className = 'obd__thumb';
      const img = document.createElement('img');
      img.alt = row.color?.value || '';
      img.width = 36;
      img.height = 36;
      if (row.thumb) img.src = row.thumb;
      thumb.append(img);

      rowEl.append(num, thumb);

      for (const role of ['color', 'size']) {
        const data = row[role];
        if (!data) continue;
        const select = document.createElement('select');
        select.className = `obd__select${role === 'size' ? ' obd__select--size' : ''}`;
        select.dataset.role = role;
        select.dataset.index = String(index);
        select.setAttribute('aria-label', data.label);
        for (const opt of data.options) {
          const option = document.createElement('option');
          option.value = opt.value;
          option.textContent = opt.value;
          option.disabled = opt.disabled;
          select.append(option);
        }
        select.value = data.value;
        rowEl.append(select);
      }
      list.append(rowEl);
    });
    return list;
  }

  #onBundleChange = (event) => {
    const bundle = this.#bundle;
    if (!bundle) return;
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      bundle.selectTier(target.value);
    } else if (target instanceof HTMLSelectElement && target.dataset.role) {
      bundle.setRowValue(Number(target.dataset.index), target.dataset.role, target.value);
    }
  };

  /* ---------- comprar ---------- */

  #onAddClick = () => {
    const mainAtc = this.#mainAtc;
    if (!mainAtc || mainAtc.disabled) return;
    mainAtc.dataset.puppet = 'true';
    mainAtc.click();
    this.#setOpen(false);
  };

  #onCartDone = (event) => {
    const reset = () => {
      const mainAtc = this.#mainAtc;
      if (mainAtc) mainAtc.dataset.puppet = 'false';
    };
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(reset);
    } else {
      reset();
    }
  };
}

if (!customElements.get('onero-quickbuy')) {
  customElements.define('onero-quickbuy', OneroQuickbuy);
}
